import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageSquare, Check, Loader2, Crown, Zap, Building2, Tag, CreditCard, Shield, AlertTriangle, X, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PLAN_ICONS = { basic: Zap, pro: Crown, agency: Building2, business: ShoppingBag };
const PLAN_COLORS = {
  basic: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600', btn: 'from-blue-600 to-blue-700', ring: 'ring-blue-200' },
  pro: { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-600', btn: 'from-purple-600 to-purple-700', ring: 'ring-purple-200' },
  agency: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600', btn: 'from-amber-600 to-amber-700', ring: 'ring-amber-200' },
  business: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-600', btn: 'from-emerald-600 to-emerald-700', ring: 'ring-emerald-200' },
};

const PAYPAL_PLAN_IDS = {
  basic: 'P-3X857991UL079831CNHM4JIQ',
  pro: 'P-4F305502WJ234605XNHM4KUI',
  agency: 'P-8B421543KN909482DNHM4LMQ',
  business: 'P-2D57802155182001YNHOB3AA',
};

const PlanSelectionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponPercent, setCouponPercent] = useState(0);
  const [couponFixedPrices, setCouponFixedPrices] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.info('Checkout cancelado. Selecciona un plan para continuar.');
    }
  }, [searchParams]);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/billing/plans`);
      setPlans(data.plans || []);
    } catch (e) {
      console.error('Error fetching plans:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  useEffect(() => {
    const checkSub = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/billing/subscription`, { withCredentials: true });
        if (data.subscription && ['active', 'trialing'].includes(data.subscription.status)) {
          navigate('/dashboard', { replace: true });
        }
      } catch (e) {}
    };
    if (user) checkSub();
  }, [user, navigate]);

  const handleSelectPlan = (planId) => {
    if (!PAYPAL_PLAN_IDS[planId]) {
      toast.error('Este plan estara disponible proximamente. Contacta soporte@acbothub.com');
      return;
    }
    setSelectedPlan(planId);
    setActivateError(null);
    setActivating(false);
  };

  const closeModal = () => {
    setSelectedPlan(null);
    setActivateError(null);
    setActivating(false);
  };

  const handlePayPalApprove = async (data) => {
    const subscriptionId = data.subscriptionID;
    if (!subscriptionId || !selectedPlan) return;

    setActivating(true);
    setActivateError(null);

    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/billing/activate-paypal`,
        { plan_id: selectedPlan, subscription_id: subscriptionId },
        { withCredentials: true }
      );
      if (resp.data.success) {
        toast.success(resp.data.message || 'Plan activado!');
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al activar tu plan. Intenta de nuevo o contacta soporte.';
      setActivateError(detail);
      setActivating(false);
    }
  };

  const handlePayPalError = (err) => {
    console.error('PayPal error:', err);
    setActivateError('Hubo un error con PayPal. Intenta de nuevo.');
    setActivating(false);
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/billing/validate-coupon`, { code: couponCode });
      if (data.valid) {
        setCouponValid(true);
        setCouponPercent(data.percent);
        setCouponFixedPrices(data.fixed_prices || null);
        toast.success(`Codigo "${data.code}" aplicado: ${data.percent}% de descuento`);
      } else {
        setCouponValid(false);
        setCouponPercent(0);
        toast.error(data.message || 'Codigo invalido');
      }
    } catch {
      setCouponValid(false);
      toast.error('Error al validar el codigo');
    }
    setValidatingCoupon(false);
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  return (
    <PayPalScriptProvider options={{
      'client-id': PAYPAL_CLIENT_ID,
      vault: true,
      intent: 'subscription',
      'enable-funding': 'card',
    }}>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-home-link">
              <MessageSquare className="w-7 h-7 text-indigo-600" />
              <span className="text-xl font-bold text-gray-950">ACBotHub</span>
            </a>
            {user && <span className="text-sm text-gray-500">{user.email}</span>}
          </div>
        </nav>

        <main className="max-w-5xl mx-auto px-6 py-12">
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-sm font-semibold px-4 py-2 rounded-full mb-6" data-testid="trial-badge">
              <Shield className="w-4 h-4" />
              7 dias de prueba gratis
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-950 mb-3" data-testid="plans-title">
              Elige tu plan
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comienza con 7 dias gratis. Se te cobrara automaticamente al finalizar el periodo de prueba. Cancela cuando quieras.
            </p>
          </div>

          {/* Credit Card Notice */}
          <div className="flex items-center justify-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-5 py-3 max-w-lg mx-auto mb-8" data-testid="cc-notice">
            <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              Paga con PayPal o tarjeta de credito/debito. <span className="font-semibold">No se realizara ningun cobro durante los primeros 7 dias.</span>
            </p>
          </div>

          {/* Coupon */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-8 max-w-lg mx-auto" data-testid="coupon-section">
            <div className="flex items-center gap-2.5 mb-3">
              <Tag className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-gray-950">Tienes un codigo de descuento?</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid="coupon-input"
                type="text"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); }}
                placeholder="Ingresa tu codigo"
                className="flex-1 bg-gray-100 border-0 rounded-lg px-4 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:bg-white transition-all placeholder:text-gray-400 placeholder:normal-case"
              />
              <Button
                size="sm"
                onClick={validateCoupon}
                disabled={validatingCoupon || !couponCode.trim()}
                data-testid="apply-coupon-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium"
              >
                {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
              </Button>
            </div>
            {couponValid === true && (
              <div className="mt-2 flex items-center gap-2 text-emerald-600 text-sm" data-testid="coupon-valid">
                <Check className="w-4 h-4" />{couponPercent}% de descuento aplicado
              </div>
            )}
            {couponValid === false && (
              <div className="mt-2 text-red-500 text-sm" data-testid="coupon-invalid">
                Codigo invalido. Verifica e intenta de nuevo.
              </div>
            )}
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="plans-grid">
            {plans.map((plan) => {
              const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.basic;
              const Icon = PLAN_ICONS[plan.id] || Zap;
              const couponDiscount = couponValid && couponPercent > 0
                ? (couponFixedPrices?.[plan.id] ?? round2(plan.price * (1 - couponPercent / 100)))
                : null;
              const isPopular = plan.id === 'pro';

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border-2 p-6 transition-all hover:shadow-lg ${isPopular ? `${colors.border} ${colors.bg} shadow-md ring-2 ${colors.ring}` : 'border-gray-200 bg-white'}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">
                      Mas Popular
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.accent}`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-950">{plan.name}</h3>
                  </div>

                  {couponDiscount !== null ? (
                    <div className="mb-4">
                      <span className="text-lg text-gray-400 line-through">${plan.price}</span>
                      <span className="text-3xl font-bold text-emerald-600 ml-2">
                        ${couponDiscount}<span className="text-sm text-gray-500 font-normal">/mes</span>
                      </span>
                      <div className="mt-1.5">
                        <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full border border-emerald-200">
                          <Check className="w-3 h-3" />-{couponPercent}% aplicado
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-950">${plan.price}</span>
                      <span className="text-sm text-gray-500 font-normal">/mes</span>
                    </div>
                  )}

                  <p className="text-xs text-indigo-600 font-medium mb-4">
                    Primeros 7 dias gratis
                  </p>

                  {plan.id === 'business' && (
                    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="business-kit-badge">
                      <p className="text-xs font-semibold text-emerald-800 leading-snug">
                        Incluye tu primer kit gratis
                      </p>
                      <p className="text-[11px] text-emerald-700 leading-snug">
                        $99 USD por cada kit adicional (pago único)
                      </p>
                    </div>
                  )}

                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className={`w-4 h-4 flex-shrink-0 ${colors.accent}`} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    data-testid={`select-plan-${plan.id}`}
                    className={`w-full bg-gradient-to-r ${colors.btn} text-white shadow-md hover:shadow-lg rounded-xl font-semibold py-2.5 transition-all`}
                  >
                    Comenzar Prueba Gratis
                  </Button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500">
            Al continuar, aceptas que se cobrara automaticamente al finalizar los 7 dias de prueba.
            Puedes cancelar en cualquier momento desde tu panel de facturacion.
          </p>

          {/* PayPal Checkout Modal */}
          {selectedPlan && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="paypal-checkout-overlay">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative" data-testid="paypal-checkout-modal">
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="close-paypal-modal"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="text-center mb-5">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-950 mb-1">
                    {selectedPlanData ? `Plan ${selectedPlanData.name}` : 'Suscribirte'}
                  </h3>
                  {selectedPlanData && (
                    <p className="text-sm text-gray-500">
                      ${selectedPlanData.price}/mes - 7 dias gratis
                    </p>
                  )}
                </div>

                {activating ? (
                  <div className="text-center py-6" data-testid="paypal-activating">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-600">Activando tu plan...</p>
                  </div>
                ) : (
                  <>
                    {activateError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="paypal-error-message">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{activateError}</p>
                      </div>
                    )}

                    <div className="mb-3">
                      <p className="text-xs text-gray-500 text-center mb-3">
                        Elige tu metodo de pago: cuenta PayPal o tarjeta de credito/debito
                      </p>
                      <div data-testid="paypal-buttons-container">
                        <PayPalButtons
                          key={selectedPlan}
                          style={{
                            shape: 'rect',
                            color: 'gold',
                            layout: 'vertical',
                            label: 'subscribe',
                          }}
                          createSubscription={(data, actions) => {
                            return actions.subscription.create({
                              plan_id: PAYPAL_PLAN_IDS[selectedPlan],
                            });
                          }}
                          onApprove={handlePayPalApprove}
                          onError={handlePayPalError}
                          onCancel={() => {
                            toast.info('Pago cancelado. Puedes intentar de nuevo.');
                          }}
                        />
                      </div>
                    </div>

                    <p className="text-xs text-gray-400 text-center">
                      Pago seguro procesado por PayPal. No se cobra durante los primeros 7 dias.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </PayPalScriptProvider>
  );
};

export default PlanSelectionPage;
